import React, { useState, useEffect } from 'react';
import axios from 'axios';
import CONFIG from '../config';

function InvoicesPage({ token }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [showEditWarning, setShowEditWarning] = useState(false);
  const [pendingEditInvoice, setPendingEditInvoice] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [allocations, setAllocations] = useState({});
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCustomer, setFilterCustomer] = useState('');
  const [form, setForm] = useState({
    customerCode: '', invoiceNo: '', invoiceDate: '',
    dueDate: '', customsRate: '', shippingStatus: 'PENDING',
    documentationStatus: 'PENDING', notes: ''
  });

  useEffect(() => {
    loadInvoices();
    loadCustomers();
  }, []);

  const loadInvoices = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${CONFIG.API}/api/bills/invoices`, {
        headers: { authorization: `Bearer ${token}` }
      });
      setInvoices(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadCustomers = async () => {
    try {
      const res = await axios.get(`${CONFIG.API}/api/bills/invoices/customers`, {
        headers: { authorization: `Bearer ${token}` }
      });
      setCustomers(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const loadPendingOrders = async (customerCode) => {
    if (!customerCode) { setPendingOrders([]); return; }
    try {
      const res = await axios.get(
        `${CONFIG.API}/api/bills/invoices/pending-orders/${customerCode}`,
        { headers: { authorization: `Bearer ${token}` } }
      );
      setPendingOrders(res.data);
      setAllocations({});
    } catch (err) {
      console.error(err);
    }
  };

  const handleChange = (e) => {
    const updated = { ...form, [e.target.name]: e.target.value };
    setForm(updated);
    if (e.target.name === 'customerCode') {
      loadPendingOrders(e.target.value);
    }
  };

  const handleAllocationChange = (orderId, value) => {
    setAllocations({ ...allocations, [orderId]: value });
  };

  const calculateWeightedAvg = () => {
    let totalFc = 0;
    let weightedSum = 0;
    pendingOrders.forEach(order => {
      const alloc = parseFloat(allocations[order.orderId] || 0);
      if (alloc > 0) {
        totalFc += alloc;
        weightedSum += alloc * parseFloat(order.costRate);
      }
    });
    if (totalFc === 0) return { totalFc: 0, weightedAvg: 0 };
    return { totalFc, weightedAvg: weightedSum / totalFc };
  };

  const resetForm = () => {
    setForm({
      customerCode: '', invoiceNo: '', invoiceDate: '',
      dueDate: '', customsRate: '', shippingStatus: 'PENDING',
      documentationStatus: 'PENDING', notes: ''
    });
    setAllocations({});
    setPendingOrders([]);
    setEditingInvoice(null);
    setShowForm(false);
  };

  // Step 1 — user clicks Edit — show warning popup first
  const handleEditClick = (invoice) => {
    if (invoice.status === 'CANCELLED' || invoice.status === 'CLOSED') {
      alert('This invoice cannot be edited.');
      return;
    }
    setPendingEditInvoice(invoice);
    setShowEditWarning(true);
  };

  // Step 2 — user confirms warning — open edit form
  const handleEditConfirm = () => {
    const invoice = pendingEditInvoice;
    setShowEditWarning(false);
    setPendingEditInvoice(null);
    setForm({
      customerCode:        invoice.customerCode || '',
      invoiceNo:           invoice.invoiceNo || '',
      invoiceDate:         invoice.invoiceDate || '',
      dueDate:             invoice.dueDate || '',
      customsRate:         invoice.customsRate || '',
      shippingStatus:      invoice.shippingStatus || 'PENDING',
      documentationStatus: invoice.documentationStatus || 'PENDING',
      notes:               typeof invoice.notes === 'string' ? invoice.notes : ''
    });
    setEditingInvoice(invoice);
    setShowForm(true);
    window.scrollTo(0, 0);
  };

  const handleSubmit = async () => {
    if (!form.invoiceNo)   { alert('Please enter invoice number'); return; }
    if (!form.invoiceDate) { alert('Please enter invoice date'); return; }
    if (!form.dueDate)     { alert('Please enter due date'); return; }
    if (!form.customsRate) { alert('Please enter customs rate'); return; }

    // EDIT mode
    if (editingInvoice) {
      try {
        await axios.put(
          `${CONFIG.API}/api/bills/invoices/${editingInvoice.invoiceId}`,
          {
            invoiceNo:           form.invoiceNo,
            invoiceDate:         form.invoiceDate,
            dueDate:             form.dueDate,
            customsRate:         parseFloat(form.customsRate),
            shippingStatus:      form.shippingStatus,
            documentationStatus: form.documentationStatus,
            notes:               form.notes || null
          },
          { headers: { authorization: `Bearer ${token}` } }
        );
        resetForm();
        loadInvoices();
      } catch (err) {
        alert('Error updating invoice. Please try again.');
      }
      return;
    }

    // NEW mode
    if (!form.customerCode) { alert('Please select a customer'); return; }

    const allocationList = pendingOrders
      .filter(o => parseFloat(allocations[o.orderId] || 0) > 0)
      .map(o => ({
        orderId:     o.orderId,
        allocatedFc: parseFloat(allocations[o.orderId])
      }));

    if (allocationList.length === 0) {
      alert('Please allocate FC amount to at least one order');
      return;
    }

    for (const o of pendingOrders) {
      const alloc = parseFloat(allocations[o.orderId] || 0);
      if (alloc > parseFloat(o.fcBalance)) {
        alert(`Allocated FC for order ${o.orderNo} exceeds available balance of ${o.fcBalance}`);
        return;
      }
    }

    try {
      await axios.post(`${CONFIG.API}/api/bills/invoices`, {
        customerCode:        form.customerCode,
        invoiceNo:           form.invoiceNo,
        invoiceDate:         form.invoiceDate,
        dueDate:             form.dueDate,
        customsRate:         parseFloat(form.customsRate),
        shippingStatus:      form.shippingStatus,
        documentationStatus: form.documentationStatus,
        notes:               form.notes || null,
        allocations:         allocationList
      }, {
        headers: { authorization: `Bearer ${token}` }
      });

      resetForm();
      loadInvoices();
      loadCustomers();
    } catch (err) {
      alert('Error saving invoice. Please check all fields.');
    }
  };

  const handleCancelInvoice = async (invoiceId) => {
    if (!window.confirm(
      'Cancel this invoice?\n\nThis will restore the FC balance back to the original orders.'
    )) return;
    try {
      await axios.put(
        `${CONFIG.API}/api/bills/invoices/${invoiceId}/cancel`, {},
        { headers: { authorization: `Bearer ${token}` } }
      );
      loadInvoices();
      loadCustomers();
    } catch (err) {
      alert('Cannot cancel — discounting or realisation already recorded.');
    }
  };

  const getStatusBadge = (status) => {
    const map = {
      'OPEN':         'badge-open',
      'PART_SETTLED': 'badge-partial',
      'CLOSED':       'badge-done',
      'CANCELLED':    'badge-cancelled'
    };
    return map[status] || 'badge-open';
  };

  const getShippingBadge  = (s) => s === 'SHIPPED'   ? 'badge-done' : 'badge-partial';
  const getDocBadge       = (s) => s === 'COMPLETED' ? 'badge-done' : 'badge-partial';

  const { totalFc, weightedAvg } = calculateWeightedAvg();

  const filteredInvoices = invoices.filter(inv => {
    const matchStatus   = filterStatus   === '' || inv.status === filterStatus;
    const matchCustomer = filterCustomer === '' ||
      inv.customerCode.toLowerCase().includes(filterCustomer.toLowerCase());
    return matchStatus && matchCustomer;
  });

  return (
    <div className="content">

      {/* Edit Warning Popup */}
      {showEditWarning && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div style={{
            background: '#fff', borderRadius: '10px', padding: '28px',
            maxWidth: '460px', width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)'
          }}>
            <div style={{ fontSize: '18px', fontWeight: '700', color: '#1E293B', marginBottom: '12px' }}>
              ⚠️ Edit Invoice
            </div>
            <div style={{ fontSize: '14px', color: '#475569', lineHeight: '1.6', marginBottom: '20px' }}>
              You can edit the following details:
              <ul style={{ marginTop: '8px', marginBottom: '8px', paddingLeft: '18px' }}>
                <li>Invoice No</li>
                <li>Invoice Date</li>
                <li>Due Date</li>
                <li>Customs Rate</li>
                <li>Shipping Status</li>
                <li>Documentation Status</li>
                <li>Notes</li>
              </ul>
              <strong>If you need to change the FC amount or order allocations</strong>, 
              please <strong>Cancel</strong> this invoice and create a new one. 
              Cancelling will automatically restore the order balances.
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                className="btn-secondary"
                onClick={() => { setShowEditWarning(false); setPendingEditInvoice(null); }}>
                Close
              </button>
              <button className="btn-primary" onClick={handleEditConfirm}>
                Yes, Edit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Summary cards */}
      <div className="metric-grid">
        <div className="metric-card">
          <div className="metric-label">Total invoices</div>
          <div className="metric-value val-blue">{invoices.length}</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Open</div>
          <div className="metric-value val-green">
            {invoices.filter(i => i.status === 'OPEN').length}
          </div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Part settled</div>
          <div className="metric-value val-amber">
            {invoices.filter(i => i.status === 'PART_SETTLED').length}
          </div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Closed</div>
          <div className="metric-value val-blue">
            {invoices.filter(i => i.status === 'CLOSED').length}
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="section-header">
        <div className="section-title">All invoices</div>
        <button className="btn-primary" onClick={() => {
          if (showForm) { resetForm(); } else { setShowForm(true); }
        }}>
          {showForm ? '✕ Close' : '+ New invoice'}
        </button>
      </div>

      {/* Form — New or Edit */}
      {showForm && (
        <div className="form-card" style={{ maxWidth: '720px' }}>
          <div className="form-step" style={{ marginBottom: '16px' }}>
            {editingInvoice ? '✏️ Edit invoice' : '📋 New invoice'}
          </div>

          {/* Row 1 — Customer + Invoice no */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
            <div className="form-field">
              <label>Customer code</label>
              {editingInvoice ? (
                <input value={form.customerCode} disabled
                  style={{ background: '#F1F5F9', color: '#94A3B8' }} />
              ) : (
                <select name="customerCode" value={form.customerCode} onChange={handleChange}>
                  <option value="">— Select customer —</option>
                  {customers.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              )}
            </div>
            <div className="form-field">
              <label>Invoice no</label>
              <input name="invoiceNo" value={form.invoiceNo}
                onChange={handleChange} placeholder="Bank / customs reference" />
            </div>
          </div>

          {/* Row 2 — Invoice date, Due date, Customs rate */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '12px' }}>
            <div className="form-field">
              <label>Invoice date (BL date)</label>
              <input type="date" name="invoiceDate" value={form.invoiceDate}
                onChange={handleChange} />
            </div>
            <div className="form-field">
              <label>Due date</label>
              <input type="date" name="dueDate" value={form.dueDate}
                onChange={handleChange} />
            </div>
            <div className="form-field">
              <label>Customs rate</label>
              <input type="number" name="customsRate" value={form.customsRate}
                onChange={handleChange} placeholder="e.g. 84.50" />
            </div>
          </div>

          {/* Row 3 — Shipping status, Documentation status */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
            <div className="form-field">
              <label>Shipping status</label>
              <select name="shippingStatus" value={form.shippingStatus} onChange={handleChange}>
                <option value="PENDING">Pending</option>
                <option value="SHIPPED">Shipped</option>
              </select>
            </div>
            <div className="form-field">
              <label>Documentation status</label>
              <select name="documentationStatus" value={form.documentationStatus} onChange={handleChange}>
                <option value="PENDING">Pending</option>
                <option value="COMPLETED">Completed</option>
              </select>
            </div>
          </div>

          {/* Order allocation table — only for new invoice */}
          {!editingInvoice && pendingOrders.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                Allocate FC from pending orders
              </div>
              <table className="data-table" style={{ marginBottom: '8px' }}>
                <thead>
                  <tr>
                    <th>Order no</th>
                    <th>Date</th>
                    <th>Cost rate</th>
                    <th>FC balance</th>
                    <th>Allocate FC</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingOrders.map(order => (
                    <tr key={order.orderId}>
                      <td>{order.orderNo}</td>
                      <td>{order.orderDate}</td>
                      <td>{Number(order.costRate).toFixed(4)}</td>
                      <td>{Number(order.fcBalance).toLocaleString()}</td>
                      <td>
                        <input
                          type="number"
                          value={allocations[order.orderId] || ''}
                          onChange={e => handleAllocationChange(order.orderId, e.target.value)}
                          placeholder="0"
                          max={order.fcBalance}
                          style={{
                            width: '110px', padding: '4px 8px',
                            border: '1px solid #CBD5E1', borderRadius: '4px',
                            fontSize: '13px'
                          }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {totalFc > 0 && (
                <div style={{
                  background: '#F0FDF4', border: '1px solid #BBF7D0',
                  borderRadius: '6px', padding: '10px 14px',
                  display: 'flex', gap: '32px', fontSize: '13px'
                }}>
                  <span><strong>Total FC allocated:</strong> {Number(totalFc).toLocaleString()}</span>
                  <span><strong>Weighted avg cost rate:</strong> {weightedAvg.toFixed(4)}</span>
                </div>
              )}
            </div>
          )}

          {/* No pending orders message */}
          {!editingInvoice && form.customerCode && pendingOrders.length === 0 && (
            <div style={{
              background: '#FEF9C3', border: '1px solid #FDE68A',
              borderRadius: '6px', padding: '10px 14px',
              fontSize: '13px', color: '#92400E', marginBottom: '16px'
            }}>
              No pending orders found for this customer.
            </div>
          )}

          {/* Notes */}
          <div style={{ marginBottom: '16px' }}>
            <div className="form-field">
              <label>Notes (optional)</label>
              <textarea
                name="notes"
                value={form.notes}
                onChange={handleChange}
                placeholder="Any additional notes..."
                rows={3}
                style={{
                  width: '100%', padding: '8px 10px',
                  border: '1px solid #CBD5E1', borderRadius: '6px',
                  fontSize: '14px', resize: 'vertical',
                  fontFamily: 'inherit', boxSizing: 'border-box'
                }}
              />
            </div>
          </div>

          {/* Buttons */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <button className="btn-secondary" onClick={resetForm}>Cancel</button>
            <button className="btn-primary" onClick={handleSubmit}>
              {editingInvoice ? '✓ Update invoice' : '✓ Save invoice'}
            </button>
          </div>
        </div>
      )}

      {/* Filter bar */}
      <div style={{
        display: 'flex', gap: '12px', alignItems: 'center',
        marginBottom: '12px', flexWrap: 'wrap'
      }}>
        <input
          type="text"
          placeholder="🔍 Search customer..."
          value={filterCustomer}
          onChange={e => setFilterCustomer(e.target.value)}
          style={{
            padding: '7px 12px', border: '1px solid #CBD5E1',
            borderRadius: '6px', fontSize: '14px', width: '200px'
          }}
        />
        <select
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
          style={{
            padding: '7px 12px', border: '1px solid #CBD5E1',
            borderRadius: '6px', fontSize: '14px', color: '#374151'
          }}
        >
          <option value="">All statuses</option>
          <option value="OPEN">Open</option>
          <option value="PART_SETTLED">Part settled</option>
          <option value="CLOSED">Closed</option>
          <option value="CANCELLED">Cancelled</option>
        </select>
        {(filterStatus !== '' || filterCustomer !== '') && (
          <button
            onClick={() => { setFilterStatus(''); setFilterCustomer(''); }}
            style={{
              padding: '7px 12px', border: '1px solid #CBD5E1',
              borderRadius: '6px', fontSize: '13px', cursor: 'pointer',
              background: '#F1F5F9', color: '#64748B'
            }}>
            ✕ Clear filters
          </button>
        )}
        <span style={{ fontSize: '13px', color: '#64748B', marginLeft: 'auto' }}>
          Showing {filteredInvoices.length} of {invoices.length} invoices
        </span>
      </div>

      {/* Invoices table */}
      <div className="table-wrap">
        {loading ? (
          <p style={{ padding: '20px', color: '#64748B' }}>Loading...</p>
        ) : filteredInvoices.length === 0 ? (
          <p style={{ padding: '20px', color: '#64748B' }}>
            No invoices found. Try changing the filters.
          </p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Invoice no</th>
                <th>Customer</th>
                <th>Invoice date</th>
                <th>Due date</th>
                <th>FC amount</th>
                <th>Customs rate</th>
                <th>Wtd cost rate</th>
                <th>FC open</th>
                <th>Shipping</th>
                <th>Docs</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredInvoices.map(inv => (
                <tr key={inv.invoiceId}>
                  <td>{inv.invoiceNo}</td>
                  <td>{inv.customerCode}</td>
                  <td>{inv.invoiceDate}</td>
                  <td>{inv.dueDate}</td>
                  <td>{Number(inv.fcAmount).toLocaleString()}</td>
                  <td>{Number(inv.customsRate).toFixed(4)}</td>
                  <td>{Number(inv.weightedAvgCostRate).toFixed(4)}</td>
                  <td>{Number(inv.fcOpen).toLocaleString()}</td>
                  <td>
                    <span className={`badge ${getShippingBadge(inv.shippingStatus)}`}>
                      {inv.shippingStatus}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${getDocBadge(inv.documentationStatus)}`}>
                      {inv.documentationStatus}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${getStatusBadge(inv.status)}`}>
                      {inv.status}
                    </span>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {inv.status !== 'CANCELLED' && inv.status !== 'CLOSED' && (
                      <button
                        className="btn-secondary"
                        onClick={() => handleEditClick(inv)}
                        style={{ marginRight: '6px' }}>
                        Edit
                      </button>
                    )}
                    {inv.status === 'OPEN' && (
                      <button
                        className="btn-danger"
                        onClick={() => handleCancelInvoice(inv.invoiceId)}>
                        Cancel
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

    </div>
  );
}

export default InvoicesPage;